import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, PanelHeader } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { INR } from "@/lib/constants";
import { Plus, RefreshCw, CheckCircle2, Trash2, Repeat, ShoppingBag } from "lucide-react";

const CATEGORIES = [
  { value: "RENT", label: "Rent" },
  { value: "MAINTENANCE", label: "Maintenance" },
  { value: "SALARIES", label: "Salaries" },
  { value: "COACH_ALLOCATION_FEE", label: "Coach Allocation Fee" },
  { value: "COACH_INDIVIDUAL_FEE", label: "Coach Individual Fee" },
  { value: "INVENTORY", label: "Inventory" },
  { value: "CONSUMABLES", label: "Consumables" },
  { value: "MISC", label: "Miscellaneous" },
];

function AddExpenseDialog({ open, onOpenChange, defaultBranch }) {
  const qc = useQueryClient();
  const { data: branches = [] } = useQuery({ queryKey: ["branches"], queryFn: async () => (await apiClient.get("/branches")).data });
  const [f, setF] = useState({
    branch_id: defaultBranch || "", category: "RENT", expense_type: "FIXED",
    amount: "", recurrence_day: "5", due_date: "", notes: "",
  });
  React.useEffect(() => { if (defaultBranch) setF((x) => ({ ...x, branch_id: defaultBranch })); }, [defaultBranch]);
  const create = useMutation({
    mutationFn: async () => (await apiClient.post("/expenses", {
      branch_id: f.branch_id, category: f.category, expense_type: f.expense_type,
      amount: parseFloat(f.amount || 0),
      recurrence_day: f.expense_type === "FIXED" ? parseInt(f.recurrence_day || 1, 10) : null,
      due_date: f.expense_type === "VARIABLE" ? (f.due_date || null) : null,
      notes: f.notes || null,
    })).data,
    onSuccess: async () => {
      toast.success("Expense created");
      qc.invalidateQueries({ queryKey: ["expenses"] });
      qc.invalidateQueries({ queryKey: ["expense-breakdown"] });
      // Auto-materialize if it was a FIXED template
      if (f.expense_type === "FIXED") {
        await apiClient.post("/expenses/materialize", {});
        qc.invalidateQueries({ queryKey: ["expenses"] });
      }
      onOpenChange(false);
      setF({ ...f, amount: "", notes: "" });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const canSubmit = f.branch_id && f.category && f.expense_type && parseFloat(f.amount) > 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm" data-testid="add-expense-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight">Add Expense</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">Fixed bills auto-repeat every month on the day you pick (Rent, Salaries etc.). One-off bills happen once (Inventory, Consumables etc.).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Branch</Label>
              <Select value={f.branch_id} onValueChange={(v) => setF({ ...f, branch_id: v })}>
                <SelectTrigger data-testid="exp-branch" className="bg-black border-zinc-800 rounded-sm mt-1"><SelectValue placeholder="Pick" /></SelectTrigger>
                <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Category</Label>
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                <SelectTrigger data-testid="exp-category" className="bg-black border-zinc-800 rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Type</Label>
              <Select value={f.expense_type} onValueChange={(v) => setF({ ...f, expense_type: v })}>
                <SelectTrigger data-testid="exp-type" className="bg-black border-zinc-800 rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="FIXED">Fixed · repeats every month</SelectItem><SelectItem value="VARIABLE">One-off · pay once</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Amount (₹)</Label>
              <Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="exp-amount" className="bg-black border-zinc-800 rounded-sm mt-1" />
            </div>
            {f.expense_type === "FIXED" ? (
              <div><Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Day of Month (1-28)</Label>
                <Input type="number" min="1" max="28" value={f.recurrence_day} onChange={(e) => setF({ ...f, recurrence_day: e.target.value })} data-testid="exp-day" className="bg-black border-zinc-800 rounded-sm mt-1" />
              </div>
            ) : (
              <div><Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Due Date</Label>
                <Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="exp-due" className="bg-black border-zinc-800 rounded-sm mt-1" />
              </div>
            )}
          </div>
          <div><Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Notes</Label>
            <Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} data-testid="exp-notes" className="bg-black border-zinc-800 rounded-sm mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={() => create.mutate()} disabled={!canSubmit || create.isPending} data-testid="save-expense" className="bg-volt hover:bg-volt-hover text-black rounded-sm">
            <Plus size={14} className="mr-1" /> Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddMerchDialog({ open, onOpenChange, defaultBranch }) {
  const qc = useQueryClient();
  const { data: branches = [] } = useQuery({ queryKey: ["branches"], queryFn: async () => (await apiClient.get("/branches")).data });
  const [f, setF] = useState({ branch_id: defaultBranch || "", product_name: "", quantity: 1, unit_price: "", notes: "" });
  React.useEffect(() => { if (defaultBranch) setF((x) => ({ ...x, branch_id: defaultBranch })); }, [defaultBranch]);
  const create = useMutation({
    mutationFn: async () => (await apiClient.post("/merch-sales", {
      branch_id: f.branch_id, product_name: f.product_name,
      quantity: parseInt(f.quantity || 1, 10), unit_price: parseFloat(f.unit_price || 0),
      notes: f.notes || null,
    })).data,
    onSuccess: () => {
      toast.success("Merch sale logged");
      qc.invalidateQueries({ queryKey: ["merch-sales"] });
      qc.invalidateQueries({ queryKey: ["revenue-breakdown"] });
      onOpenChange(false); setF({ ...f, product_name: "", quantity: 1, unit_price: "", notes: "" });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm" data-testid="add-merch-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase tracking-tight">Log Merch Sale</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Branch</Label>
            <Select value={f.branch_id} onValueChange={(v) => setF({ ...f, branch_id: v })}>
              <SelectTrigger data-testid="merch-branch" className="bg-black border-zinc-800 rounded-sm mt-1"><SelectValue placeholder="Pick" /></SelectTrigger>
              <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Product</Label>
            <Input value={f.product_name} onChange={(e) => setF({ ...f, product_name: e.target.value })} data-testid="merch-name" className="bg-black border-zinc-800 rounded-sm mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Qty</Label>
              <Input type="number" min="1" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} data-testid="merch-qty" className="bg-black border-zinc-800 rounded-sm mt-1" />
            </div>
            <div><Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Unit ₹</Label>
              <Input type="number" value={f.unit_price} onChange={(e) => setF({ ...f, unit_price: e.target.value })} data-testid="merch-price" className="bg-black border-zinc-800 rounded-sm mt-1" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={() => create.mutate()} disabled={!f.branch_id || !f.product_name || !f.unit_price || create.isPending}
            data-testid="save-merch" className="bg-volt hover:bg-volt-hover text-black rounded-sm">Log Sale</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ExpensesPage() {
  const { user } = useAuth();
  const [dlgOpen, setDlgOpen] = useState(false);
  const [merchOpen, setMerchOpen] = useState(false);
  const monthKey = React.useMemo(() => {
    const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`;
  }, []);
  const [showTemplates, setShowTemplates] = useState(false);
  const { data: rows = [] } = useQuery({
    queryKey: ["expenses", monthKey, showTemplates],
    queryFn: async () => (await apiClient.get(`/expenses?month=${monthKey}${showTemplates ? "&include_templates=true" : ""}`)).data,
  });
  const { data: breakdown } = useQuery({
    queryKey: ["expense-breakdown", monthKey],
    queryFn: async () => (await apiClient.get(`/finance/expense-breakdown`)).data,
  });
  const qc = useQueryClient();
  const rematerialize = useMutation({
    mutationFn: async () => (await apiClient.post("/expenses/materialize", {})).data,
    onSuccess: (d) => {
      const msg = d.created > 0
        ? `Generated ${d.created} bill${d.created > 1 ? "s" : ""} from your recurring schedules`
        : `All ${d.template_count} recurring bill${d.template_count === 1 ? "" : "s"} already exist for this month`;
      toast.success(msg);
      qc.invalidateQueries({ queryKey: ["expenses"] });
    },
  });
  const del = useMutation({
    mutationFn: async (id) => (await apiClient.delete(`/expenses/${id}`)).data,
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["expenses"] }); qc.invalidateQueries({ queryKey: ["expense-breakdown"] }); },
  });
  const markPaid = useMutation({
    mutationFn: async (id) => (await apiClient.patch(`/expenses/${id}`, { paid: true })).data,
    onSuccess: () => { toast.success("Marked paid"); qc.invalidateQueries({ queryKey: ["expenses"] }); qc.invalidateQueries({ queryKey: ["expense-breakdown"] }); },
  });

  const defaultBranch = (user?.branch_ids || [])[0];
  const badge = (paid) => paid ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400";

  return (
    <div className="p-8 space-y-6 animate-fade-in-up" data-testid="expenses-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-volt font-semibold">Finance · {monthKey}</div>
          <h1 className="font-display text-5xl font-black uppercase tracking-tight mt-1">Expenses & Merch</h1>
          <div className="text-zinc-500 text-sm mt-1">Set up recurring bills (like Rent, Salaries) once — they'll appear automatically each month. Add one-off expenses (like Inventory) whenever needed.</div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setMerchOpen(true)} data-testid="log-merch-btn" className="rounded-sm border-zinc-800">
            <ShoppingBag size={14} className="mr-1" /> Log Merch Sale
          </Button>
          <Button variant="outline" onClick={() => rematerialize.mutate()} data-testid="materialize-btn" className="rounded-sm border-zinc-800">
            <RefreshCw size={14} className="mr-1" /> Refresh Monthly Bills
          </Button>
          <Button onClick={() => setDlgOpen(true)} data-testid="add-expense-btn" className="bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-wider rounded-sm">
            <Plus size={14} className="mr-1" /> Add Expense
          </Button>
        </div>
      </div>

      <AddExpenseDialog open={dlgOpen} onOpenChange={setDlgOpen} defaultBranch={defaultBranch} />
      <AddMerchDialog open={merchOpen} onOpenChange={setMerchOpen} defaultBranch={defaultBranch} />

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-zinc-950 border border-zinc-900 rounded-sm p-5" data-testid="exp-total-tile">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold">Total Expenses</div>
          <div className="font-display text-5xl font-black text-volt mt-2 leading-none">{INR(breakdown?.total || 0)}</div>
          <div className="text-xs text-zinc-500 mt-1">{breakdown?.count || 0} entries</div>
        </div>
        <div className="bg-zinc-950 border border-zinc-900 rounded-sm p-5" data-testid="exp-paid-tile">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold">Paid</div>
          <div className="font-display text-5xl font-black text-green-400 mt-2 leading-none">{INR(breakdown?.paid || 0)}</div>
        </div>
        <div className="bg-zinc-950 border border-zinc-900 rounded-sm p-5" data-testid="exp-outstanding-tile">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold">Outstanding</div>
          <div className="font-display text-5xl font-black text-yellow-400 mt-2 leading-none">{INR(breakdown?.outstanding || 0)}</div>
        </div>
      </div>

      {/* Expenses list */}
      <Panel data-testid="expenses-list-panel">
        <PanelHeader title={showTemplates ? "This Month's Bills + Recurring Schedules" : "This Month's Bills"}
          subtitle={`${rows.length} ${rows.length === 1 ? "row" : "rows"}`}
          action={
            <Button size="sm" variant="ghost" onClick={() => setShowTemplates((x) => !x)} data-testid="toggle-templates" className="text-xs">
              <Repeat size={12} className="mr-1" /> {showTemplates ? "Hide recurring schedules" : "Show recurring schedules"}
            </Button>
          }
        />
        {rows.length === 0 ? (
          <div className="text-zinc-500 text-sm py-8 text-center">No expenses yet — click Add Expense.</div>
        ) : (
          <div className="divide-y divide-zinc-900">
            {rows.map((e) => (
              <div key={e.id} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`expense-${e.id}`}>
                <div className={`text-[9px] uppercase tracking-[0.16em] font-bold px-2 py-0.5 rounded-sm ${badge(e.paid)}`}>{e.paid ? "PAID" : "PENDING"}</div>
                {e.is_template && <div className="text-[9px] uppercase tracking-[0.16em] font-bold px-2 py-0.5 rounded-sm bg-volt/10 text-volt border border-volt/30">RECURS ON DAY {e.recurrence_day}</div>}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{CATEGORIES.find(c => c.value === e.category)?.label || e.category}</div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500">
                    {e.branch_name} · {e.expense_type === "FIXED" ? "Fixed" : "One-off"} · {e.due_date ? `Due ${e.due_date}` : `Day ${e.recurrence_day} of every month`}
                    {e.notes ? ` · ${e.notes}` : ""}
                  </div>
                </div>
                <div className="font-display text-xl font-black text-volt">{INR(e.amount)}</div>
                {!e.paid && !e.is_template && (
                  <Button size="sm" onClick={() => markPaid.mutate(e.id)} data-testid={`mark-paid-${e.id}`}
                    className="bg-green-500 hover:bg-green-400 text-black rounded-sm text-[10px] uppercase tracking-[0.14em]">
                    <CheckCircle2 size={12} className="mr-1" /> Paid
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => del.mutate(e.id)} data-testid={`del-exp-${e.id}`} className="text-red-400 hover:bg-red-500/10 rounded-sm">
                  <Trash2 size={12} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
